"use client";

import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  Dispatch,
  SetStateAction,
  KeyboardEvent,
  ClipboardEvent,
  ChangeEvent,
} from "react";
import { useLocationBarcodeVerifyMutation } from "../../../hooks/inventoryHooks";
import toast from "react-hot-toast";
import type { UseFormSetValue } from "react-hook-form";
import { useLanguage, T } from "../../../context/LanguageContext";

// --- PROPS TYPE ---
type AssignModalProps = {
  setValue: UseFormSetValue<any>; // From react-hook-form
  awbText: string;
  setAwbText: Dispatch<SetStateAction<string>>;
  locationId: string;
  setLocationId: Dispatch<SetStateAction<string>>;
  warehouseId?: string;
};

export const useBarcodeScanner = (onScan: (code: string) => void) => {
  const onScanCallback = useRef(onScan);
  const isPasting = useRef(false);

  useEffect(() => {
    onScanCallback.current = onScan;
  }, [onScan]);

  useEffect(() => {
    let buffer = "";
    let lastKeyTime = 0;
    let timeout: NodeJS.Timeout | null = null;

    // --- Global paste handler to block scanner briefly ---
    const handlePaste = () => {
      isPasting.current = true;
      setTimeout(() => {
        isPasting.current = false;
      }, 150); // Block for 150ms after paste
    };

    // Attach to document (captures paste in any input/textarea)
    document.addEventListener("paste", handlePaste);

    const handleKeydown = (e: KeyboardEvent) => {
      const now = Date.now();

      // --- BLOCK ALL KEYS DURING PASTE WINDOW ---
      if (isPasting.current) {
        return;
      }

      // --- PASTE SHORTCUT: Ctrl+V or Cmd+V → IGNORE ---
      if ((e.ctrlKey || e.metaKey) && e.key === "v") {
        return;
      }

      // --- ENTER: Submit buffer ---
      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        if (buffer.length > 0) {
          onScanCallback.current(buffer.trim());
        }
        buffer = "";
        if (timeout) clearTimeout(timeout);
        return;
      }

      // --- Printable ASCII keys ---
      if (
        /^[\x20-\x7E]$/.test(e.key) &&
        e.key.length === 1 &&
        !e.ctrlKey &&
        !e.metaKey
      ) {
        if (now - lastKeyTime > 200) buffer = "";
        buffer += e.key;
        lastKeyTime = now;

        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => {
          if (buffer.length > 0) {
            onScanCallback.current(buffer.trim());
          }
          buffer = "";
        }, 500);
      }
    };

    window.addEventListener("keydown", handleKeydown as any, true);

    return () => {
      window.removeEventListener("keydown", handleKeydown as any, true);
      document.removeEventListener("paste", handlePaste);
      if (timeout) clearTimeout(timeout);
    };
  }, []);
};

// --- MAIN COMPONENT ---
const AddInventoryModal: React.FC<AssignModalProps> = ({
  setValue,
  awbText,
  setAwbText,
  setLocationId,
  warehouseId,
}) => {
  const { t } = useLanguage();
  const [isShelfVerified, setIsShelfVerified] = useState(false);
  const [shelfId, setShelfId] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [shelfManualInput, setShelfManualInput] = useState<string>("");

  const shelfInputRef = useRef<HTMLInputElement>(null);
  const processingRef = useRef(false);

  const { mutateAsync: verifyLocationBarcode } = useLocationBarcodeVerifyMutation();

  // --- Reset Shelf ---
  const resetShelf = useCallback(() => {
    setShelfId("");
    setLocationId("");
    setIsShelfVerified(false);
    setAwbText("");
    setShelfManualInput("");
    setValue("targetShelfId", "");
    setValue("awbs", []);
    setTimeout(() => shelfInputRef.current?.focus(), 0);
  }, [setLocationId, setAwbText, setValue]);

  // --- Sync AWBs to Form (debounced, safe) ---
  useEffect(() => {
    const awbArray = awbText
      .split(/[\s,\n]+/)
      .map((s) => s.trim())
      .filter(Boolean);

    setValue("awbs", awbArray, { shouldDirty: true });
  }, [awbText, setValue]);

  const totalAwbs = awbText.split(/[\s,\n]+/).filter(Boolean).length;

  // --- Verify Shelf Barcode ---
  const handleVerifyBarcode = useCallback(
    async (barcode: string): Promise<boolean> => {
      const trimmed = barcode.trim();
      if (!trimmed) return false;

      try {
        const res = await verifyLocationBarcode({
          barcodeId: trimmed,
          warehouseId: warehouseId || undefined,
          allowedLocationType: "LEVELS",
        });
        const locationId = res?.barcodeLabel || (typeof res === "string" ? res : String(res));

        setShelfId(locationId);
        setLocationId(trimmed);
        setIsShelfVerified(true);
        setShelfManualInput("");
        setValue("targetShelfId", trimmed);
        toast.success(t("Shelf verified!"));
        return true;
      } catch (error: any) {
        toast.error(error.message || t("Invalid shelf barcode"));
        return false;
      }
    },
    [verifyLocationBarcode, setLocationId, setValue, warehouseId, t]
  );

  // --- Barcode Scanner Handler (Stable with useCallback + ref) ---
  const handleScannedBarcode = useCallback(
    async (barcode: string) => {
      if (processingRef.current) return;
      processingRef.current = true;
      setIsProcessing(true);

      try {
        if (!isShelfVerified) {
          await handleVerifyBarcode(barcode);
        } else {
          setAwbText((prev) => (prev ? `${prev}\n${barcode}` : barcode));
          toast.success(`${t("AWB scanned")}: ${barcode}`);
        }
      } finally {
        processingRef.current = false;
        setIsProcessing(false);
      }
    },
    [isShelfVerified, handleVerifyBarcode, t]
  );

  // --- Attach Scanner ---
  useBarcodeScanner(handleScannedBarcode);

  // --- Manual Shelf Input ---
  const handleShelfKeyDown = async (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && shelfManualInput.trim()) {
      e.preventDefault();
      setIsProcessing(true);
      await handleVerifyBarcode(shelfManualInput.trim());
      setIsProcessing(false);
    }
  };

  const handleShelfPaste = async (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").trim().split(/[\s,\n]+/, 1)[0];
    if (!pasted) return;

    setShelfManualInput(pasted);
    setIsProcessing(true);
    await handleVerifyBarcode(pasted);
    setIsProcessing(false);
  };

  const handleShelfChange = (e: ChangeEvent<HTMLInputElement>) => {
    setShelfManualInput(e.target.value);
  };

  // --- AWB Paste ---
  const handleAwbPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    if (!isShelfVerified) {
      toast.error(t("Verify shelf first"));
      e.preventDefault();
      return;
    }

    e.preventDefault();
    const pasted = e.clipboardData.getData("text");
    const cleaned = pasted
      .split(/[\s,\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .join("\n");

    setAwbText((prev) => (prev ? `${prev}\n${cleaned}` : cleaned));
    toast.success(`${cleaned.split("\n").length} ${t("AWB(s) pasted")}`);
  };

  const handleAwbChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setAwbText(e.target.value);
  };

  // --- Auto-focus on mount ---
  useEffect(() => {
    shelfInputRef.current?.focus();
  }, []);

  return (
    <div className="w-full py-4 px-6 space-y-4 bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] rounded-tr-[30px]">
      <div className="h-[calc(100vh-13rem)] overflow-y-auto space-y-4">
        {/* Shelf Section */}
        <div className="bg-[#FCFDF8] rounded-xl p-3 border border-[#D2D2D2]">
          <div className="flex justify-between items-center">
            <h2 className="text-[#212121] text-base font-medium"><T>Shelf ID</T></h2>
            {isShelfVerified && (
              <button
                onClick={resetShelf}
                className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
                disabled={isProcessing}
              >
                <T>Reset Shelf</T>
              </button>
            )}
          </div>

          {isShelfVerified ? (
            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-lg font-bold text-green-800">{shelfId}</p>
            </div>
          ) : (
            <div className="mt-2">
              <input
                ref={shelfInputRef}
                type="text"
                className="w-full h-12 px-3 text-lg border-b-2 border-gray-300 focus:border-blue-500 outline-none bg-transparent placeholder-[#212121]/50"
                placeholder={t("Scan, paste, or type shelf ID...")}
                value={shelfManualInput}
                onChange={handleShelfChange}
                onKeyDown={handleShelfKeyDown}
                onPaste={handleShelfPaste}
                disabled={isProcessing}
                autoFocus
              />
              {isProcessing && (
                <p className="mt-1 text-sm text-blue-600 animate-pulse">
                  <T>Verifying shelf...</T>
                </p>
              )}
            </div>
          )}
        </div>

        {/* AWB Section */}
        <div className="bg-[#FCFDF8] rounded p-2 space-y-2">
          <div className="flex justify-between items-center">
            <h2 className="text-[#212121] text-base font-medium">
              {t("Shipments")} ({totalAwbs})
            </h2>
          </div>

          <div className="bg-[#FCFDF8] border border-[#D2D2D2] rounded-xl p-4 h-[calc(100vh-26rem)]">
            {isShelfVerified ? (
              <textarea
                id="awb-textarea"
                className="w-full h-full resize-none outline-none bg-transparent text-sm text-[#212121]/80 placeholder-[#212121]/40"
                placeholder={t("Paste AWBs, scan them, or type...")}
                value={awbText}
                onChange={handleAwbChange}
                onPaste={handleAwbPaste}
                aria-label="Enter or scan AWB numbers"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-center">
                <p className="text-lg font-medium text-[#212121]/50">
                  <T>Scan shelf first</T>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddInventoryModal;